import React, { useCallback } from 'react';
import FieldShell from './FieldShell';
import { useDraftField } from '../hooks/useDraftField';
import { useFieldLifecycle } from '../hooks/useFieldLifecycle';

function defaultFormat(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

function parseNumericDraft(draft, allowDecimal) {
  const trimmed = String(draft ?? '').trim();
  if (trimmed === '') {
    return { kind: 'empty' };
  }

  const pattern = allowDecimal
    ? /^[+-]?(?:\d+|\d*\.\d+)$/
    : /^[+-]?\d+$/;

  if (!pattern.test(trimmed)) {
    return { kind: 'invalid' };
  }

  const parsed = allowDecimal ? Number(trimmed) : parseInt(trimmed, 10);
  if (!Number.isFinite(parsed)) {
    return { kind: 'invalid' };
  }

  return { kind: 'value', value: parsed };
}

export default function NumberField({
  value,
  onCommit,
  format = defaultFormat,
  emptyValue = null,
  min,
  max,
  allowDecimal = false,
  allowNegative = false,
  normalizeValue = null,
  label,
  helpText,
  error,
  inputId,
  width,
  tapTarget,
  startAdornment,
  endAdornment,
  onBlur,
  onKeyDown,
  onChange,
  flushOnBlur = true,
  cancelOnEscape = false,
  submitOnEnter = false,
  blurAfterSubmit = false,
  blurAfterCancel = false,
  unmountPolicy = 'cancel',
  type = 'text',
  inputMode,
  ...rest
}) {
  const normalizeCommitValue = useCallback((nextDraft) => {
    const parsed = parseNumericDraft(nextDraft, allowDecimal);

    if (parsed.kind === 'empty') {
      return emptyValue;
    }

    if (parsed.kind === 'invalid') {
      return value;
    }

    let nextValue = parsed.value;

    if (!allowNegative && nextValue < 0) {
      nextValue = typeof min === 'number' ? min : 0;
    }

    if (typeof min === 'number') {
      nextValue = Math.max(min, nextValue);
    }

    if (typeof max === 'number') {
      nextValue = Math.min(max, nextValue);
    }

    if (normalizeValue) {
      nextValue = normalizeValue(nextValue);
    }

    return nextValue;
  }, [allowDecimal, allowNegative, emptyValue, max, min, normalizeValue, value]);

  const handleCommit = useCallback((nextDraft) => {
    const nextValue = normalizeCommitValue(nextDraft);
    if (!Object.is(nextValue, value)) {
      onCommit(nextValue);
    }
  }, [normalizeCommitValue, onCommit, value]);

  const { draft, cancel, flush, updateDraft } = useDraftField({
    value,
    onCommit: handleCommit,
    format,
    unmountPolicy,
  });

  const { handleBlur, handleKeyDown } = useFieldLifecycle({
    flush,
    cancel,
    onBlur,
    onKeyDown,
    flushOnBlur,
    cancelOnEscape,
    submitOnEnter,
    blurAfterSubmit,
    blurAfterCancel,
  });

  const handleChange = useCallback((event) => {
    updateDraft(event.target.value);
    if (onChange) onChange(event);
  }, [onChange, updateDraft]);

  const resolvedInputMode = inputMode || (type === 'text'
    ? (allowDecimal ? 'decimal' : 'numeric')
    : undefined);

  const control = (
    <input
      {...rest}
      type={type}
      inputMode={resolvedInputMode}
      value={draft}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );

  const shouldWrapWithShell = !!(label || helpText || error || inputId || width || tapTarget || startAdornment || endAdornment);
  if (!shouldWrapWithShell) {
    return control;
  }

  return (
    <FieldShell
      label={label}
      helpText={helpText}
      error={error}
      inputId={inputId}
      width={width}
      tapTarget={tapTarget}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
    >
      {control}
    </FieldShell>
  );
}
